import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = {
  full_name: "",
  company_name: "",
  phone: "",
  email: "",
  service: "",
  location: "",
  message: "",
  website: "",
};

export const EnquiryForm = ({ defaultService = "", quoteMode = false }) => {
  const { content } = useSite();
  const services = content?.services || [];
  const [form, setForm] = useState({ ...EMPTY, service: defaultService });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (defaultService) setForm((f) => ({ ...f, service: defaultService }));
  }, [defaultService]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
    if (!/^[+0-9][0-9\s-]{5,}$/.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.service) e.service = "Please select the service you need.";
    if (form.message.trim().length < 5) e.message = "Please tell us a little about your requirement.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent successfully. Our team will reach out to you shortly.");
      setForm({ ...EMPTY, service: defaultService });
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your enquiry right now. Please try again or call us directly."));
    } finally {
      setSubmitting(false);
    }
  };

  const field = "h-12 rounded-lg border-slate-300 bg-white focus-visible:ring-gold";
  const err = (k) => errors[k] && <p className="mt-1 text-xs font-medium text-red-600">{errors[k]}</p>;

  return (
    <form onSubmit={submit} noValidate data-testid="enquiry-form" className="grid gap-5 md:grid-cols-2">
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={(e) => set("website", e.target.value)}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <div>
        <Label htmlFor="enq-name" className="mb-1.5 block text-sm font-semibold text-navy-900">
          Full Name <span className="text-gold-dark">*</span>
        </Label>
        <Input
          id="enq-name"
          data-testid="enquiry-name-input"
          className={field}
          placeholder="Your full name"
          value={form.full_name}
          onChange={(e) => set("full_name", e.target.value)}
        />
        {err("full_name")}
      </div>
      <div>
        <Label htmlFor="enq-company" className="mb-1.5 block text-sm font-semibold text-navy-900">
          Company Name
        </Label>
        <Input
          id="enq-company"
          data-testid="enquiry-company-input"
          className={field}
          placeholder="Company or organization (optional)"
          value={form.company_name}
          onChange={(e) => set("company_name", e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="enq-phone" className="mb-1.5 block text-sm font-semibold text-navy-900">
          Phone Number <span className="text-gold-dark">*</span>
        </Label>
        <Input
          id="enq-phone"
          data-testid="enquiry-phone-input"
          className={field}
          placeholder="+91 ..."
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
        />
        {err("phone")}
      </div>
      <div>
        <Label htmlFor="enq-email" className="mb-1.5 block text-sm font-semibold text-navy-900">
          Email Address <span className="text-gold-dark">*</span>
        </Label>
        <Input
          id="enq-email"
          type="email"
          data-testid="enquiry-email-input"
          className={field}
          placeholder="you@company.com"
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
        />
        {err("email")}
      </div>
      <div>
        <Label htmlFor="enq-service" className="mb-1.5 block text-sm font-semibold text-navy-900">
          Service Required <span className="text-gold-dark">*</span>
        </Label>
        <Select value={form.service} onValueChange={(v) => set("service", v)}>
          <SelectTrigger id="enq-service" data-testid="enquiry-service-select" className={field}>
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {services.map((s) => (
              <SelectItem key={s.slug} value={s.title}>
                {s.title}
              </SelectItem>
            ))}
            <SelectItem value="General Enquiry">General Enquiry</SelectItem>
          </SelectContent>
        </Select>
        {err("service")}
      </div>
      <div>
        <Label htmlFor="enq-location" className="mb-1.5 block text-sm font-semibold text-navy-900">
          Location
        </Label>
        <Input
          id="enq-location"
          data-testid="enquiry-location-input"
          className={field}
          placeholder="City / area (optional)"
          value={form.location}
          onChange={(e) => set("location", e.target.value)}
        />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="enq-message" className="mb-1.5 block text-sm font-semibold text-navy-900">
          Message <span className="text-gold-dark">*</span>
        </Label>
        <Textarea
          id="enq-message"
          data-testid="enquiry-message-input"
          className="min-h-[130px] rounded-lg border-slate-300 bg-white focus-visible:ring-gold"
          placeholder={quoteMode ? "Tell us what you need, approximate scale, and timelines..." : "How can we help you?"}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
        />
        {err("message")}
      </div>
      <div className="md:col-span-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="enquiry-submit-btn"
          className="group flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-navy-950 transition-all duration-300 hover:bg-gold-light hover:shadow-lg hover:shadow-gold/25 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending...
            </>
          ) : (
            <>
              {quoteMode ? "Request Quote" : "Send Enquiry"}
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};
